/**
 * EarlyAccessSignup — LAUNCH-EA-1 public waitlist capture, at
 * /early-access.
 *
 * Where the marketing site's "Early access" links point, and where
 * Auth.tsx bounces a brand-new OAuth signup that isn't on the
 * approved waitlist yet (see waitlist-guard). Visually a sibling of
 * Auth.tsx — same black stage, same AuthCollageBackground, same
 * field/button primitives — so the hop from marketing to here reads
 * as one continuous surface, not a detour.
 *
 * Aug 28 — rebuilt the header + form as one flowing column instead of
 * an absolutely-positioned header floating over a form whose top
 * padding GUESSED how tall that header would be. The guess didn't
 * budget for this page's longer description paragraph wrapping to a
 * 3rd line on some viewports, so the text ran straight into the name
 * field below it. Flow layout can't overlap regardless of how long
 * the text runs or how narrow the viewport is — see the same fix on
 * Auth.tsx's hero.
 *
 * Also handles the redirect Auth.tsx sends a blocked OAuth attempt
 * to: ?email=<email>&blocked=1 swaps the pitch copy for a short
 * "sign-in is invite-only" explanation instead of the generic "Get
 * early access" hook, since that visitor didn't arrive here by choice
 * — see the `user` effect in Auth.tsx and waitlist-guard's header for
 * the full mechanism.
 *
 * Posts to the waitlist-signup edge function (public, service role
 * on the other side) which writes to waitlist_signups and sends a
 * confirmation email. No account is created here — see that
 * function's header and src/pages/admin/AdminEarlyAccess.tsx for the
 * invite half of the flow.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { AuthCollageBackground } from '@/components/auth/AuthCollageBackground';
import orbitLogoWhite from '@/assets/orbit-retro-logo-white.png';
import { NovaSpinner } from '@/components/nova';
import { track } from '@/lib/analytics';

const signupSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  name: z.string().trim().max(200).optional(),
});

export default function EarlyAccessSignup() {
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') ?? '';
  const blocked = searchParams.get('blocked') === '1';

  const [email, setEmail] = useState(prefillEmail);
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // FREEWEEK-1 — Mission Control's funnel.
  useEffect(() => {
    track('early_access_viewed', { blocked });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setErrorMessage(null);

    const result = signupSchema.safeParse({ email, name: name || undefined });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setStatus('loading');
    try {
      const { data, error } = await supabase.functions.invoke('waitlist-signup', {
        body: { email: result.data.email, name: result.data.name },
      });
      if (error || !data?.ok) {
        setStatus('error');
        setErrorMessage(
          (data && 'error' in data ? (data.error as string) : undefined) ||
            "Couldn't join the waitlist — try again in a minute.",
        );
        return;
      }
      setStatus('done');
      track('waitlist_joined');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : "Couldn't join the waitlist.");
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-black text-white">
      <AuthCollageBackground />

      {/* ── Content column — logo, pitch, and form all flow in one
          column instead of the header floating separately over a
          height-guessing form. See file header for why. ──────────── */}
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col items-center px-6 pt-[12vh] pb-10">
        <img
          src={orbitLogoWhite}
          alt="Orbit"
          className="h-[120px] w-auto shrink-0 drop-shadow-[0_6px_28px_rgba(0,0,0,0.65)]"
        />

        {blocked ? (
          <>
            <p className="mt-5 max-w-[30ch] text-center font-editorial text-[clamp(20px,3.2vw,28px)] font-normal italic leading-[1.2] text-white/90">
              Not quite yet.
            </p>
            <p className="mt-3 max-w-[40ch] text-center text-[15px] leading-[1.5] text-white/60">
              Sign-in is invite-only while we're working through the waitlist.{' '}
              {prefillEmail ? (
                <>
                  We've added <span className="text-white/85">{prefillEmail}</span> to the
                  waitlist —
                </>
              ) : (
                <>Join the waitlist and</>
              )}{' '}
              we'll email you the moment it's your turn — usually within a week.
            </p>
          </>
        ) : (
          <>
            <p className="mt-5 max-w-[26ch] text-center font-editorial text-[clamp(22px,3.4vw,30px)] font-normal italic leading-[1.2] text-white/90">
              Join the waitlist.
            </p>
            <p className="mt-3 max-w-[38ch] text-center text-[15px] leading-[1.5] text-white/60">
              {/* LAUNCH-2 — copy rule: never "studios" for the
                  customer's operation.

                  WAITLIST-1 — this page is REACHABLE again: App.tsx
                  routes /early-access here whenever IS_EARLY_ACCESS is
                  true, and WAITLIST_GATE has been true since LAUNCH-3.
                  An older comment here said the opposite; it was stale,
                  which is how the September 14 line survived past
                  September 14 in front of every visitor.

                  "within a week", never "7 days" — the pricing page
                  sells a 7-day free trial, and one visitor reading both
                  should not have to work out which seven days is which.
                  The numeral belongs to the trial. */}
              We're opening Orbit to new businesses in small batches. Most
              people are in <span className="text-white/85">within a week</span>
              {' '}— we'll email you the moment it's your turn.
            </p>
          </>
        )}

        <div className="mt-8 w-full">
          {status === 'done' ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-6 py-8 text-center backdrop-blur-md">
              <p className="font-editorial text-2xl italic text-white">You're on the list.</p>
              <p className="text-[14px] leading-relaxed text-white/65">
                Check your inbox at <span className="text-white/90">{email}</span> for a
                confirmation. We'll be in touch as we open things up.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Field
                id="ea-name"
                label="Your name (optional)"
                type="text"
                value={name}
                onChange={setName}
                placeholder="Jane Doe"
                disabled={status === 'loading'}
                error={errors.name}
                autoComplete="name"
              />
              <Field
                id="ea-email"
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                disabled={status === 'loading'}
                error={errors.email}
                autoComplete="email"
              />
              {status === 'error' && errorMessage && (
                <p className="text-[13px] text-red-300/90">{errorMessage}</p>
              )}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-2 flex w-full items-center justify-center rounded-2xl py-4 text-[17px] font-semibold text-black transition-opacity disabled:opacity-70"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                {status === 'loading' ? (
                  <>
                    <NovaSpinner size={16} className="mr-2" />
                    Joining…
                  </>
                ) : (
                  'Join the waitlist'
                )}
              </button>
            </form>
          )}
        </div>

        <div className="mt-auto flex flex-col items-center gap-2 pt-10 text-center text-[14px] text-white/60">
          <p>
            Already got an invite?{' '}
            <Link
              to="/auth"
              className="font-medium text-white underline-offset-4 hover:underline"
            >
              Sign in
            </Link>
          </p>
          <Link to="/orbit" className="text-white/40 underline-offset-4 hover:text-white/70 hover:underline">
            ← Back to Orbit
          </Link>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  autoComplete?: string;
}

function Field({ id, label, type, value, onChange, placeholder, disabled, error, autoComplete }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium text-white/75">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        className="w-full rounded-xl border border-white/20 bg-black/45 backdrop-blur-md px-4 py-3 text-[15px] text-white placeholder:text-white/40 focus:border-white/40 focus:outline-none disabled:opacity-60"
      />
      {error && <p className="pl-1 text-[12px] text-red-300/90">{error}</p>}
    </div>
  );
}
